import {
  isArtifactShareActive,
  type ArtifactShareView,
} from '@/lib/api/artifact-shares.js';

/**
 * What a folded-away list of shares still has to say out loud.
 *
 * The shares list is the one part of the share dialog that grows without bound, so it is collapsed — and
 * a collapsed list is only honest if its summary carries the facts somebody would otherwise miss. There
 * are exactly two: how many links are open, and how many of them told nobody.
 */
export interface ArtifactShareSummary {
  /** Shares that still open — the same set the expanded list draws. */
  liveCount: number;
  /** Live shares whose notification failed. The author's only sign that nobody was told. */
  notDeliveredCount: number;
}

/**
 * Counts a file's shares the way the list draws them: a revoked or expired share is not a share, and
 * whatever went wrong notifying one is no longer news once it grants nothing.
 */
export function summarizeArtifactShares(
  shares: readonly ArtifactShareView[],
): ArtifactShareSummary {
  const live = shares.filter((share) => isArtifactShareActive(share));
  return {
    liveCount: live.length,
    notDeliveredCount: live.filter((share) => share.notifyError !== null)
      .length,
  };
}

/** The marker beside that row when a notification failed — short, because it sits on one line. */
export function notDeliveredLabel(count: number): string {
  return `${count} not delivered`;
}
